import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { MessageSquare, Send, Lock, Search, X, Pin, Hash, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { EmojiPickerButton } from '@/components/EmojiPicker';
import TierPaymentModal from '@/components/TierPaymentModal';
import MentionAutocomplete from '@/components/MentionAutocomplete';
import { SpeechToTextIcon } from '@/components/SpeechToText';
import { Pagination } from '@/components/Pagination';
import { ChatMessage } from '@/components/forum/ChatMessage';
import { useLanguage, useTranslatedMap, TranslatableText } from '@/contexts/LanguageContext';

const ForumPage = ({ user }) => {
  const [categories, setCategories] = useState([]);
  const [connectedIds, setConnectedIds] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [forumPage, setForumPage] = useState(1);
  const [forumTotalPages, setForumTotalPages] = useState(1);
  const [forumTotalPosts, setForumTotalPosts] = useState(0);
  const [messageText, setMessageText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [membershipType, setMembershipType] = useState(user?.membership_tier || null);
  const [showTierPaymentModal, setShowTierPaymentModal] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportPostId, setReportPostId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');

  const selectedCategoryRef = useRef(null);
  const pageRef = useRef(1);
  selectedCategoryRef.current = selectedCategory;
  pageRef.current = forumPage;

  // A platform link can point at one message, not just the room it lives in.
  const [linkedPostId] = useState(
    () => new URLSearchParams(window.location.search).get('post') || null
  );

  useEffect(() => {
    if (!linkedPostId || !posts.length) return;
    const el = document.getElementById(`post-${linkedPostId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [linkedPostId, posts]);

  // Lead ids for the active channel (Section Lead badges + pin permission)
  const leadIds = useMemo(() => {
    if (!selectedCategory) return new Set();
    const ids = [selectedCategory.lead_practitioner_id, ...(selectedCategory.co_lead_ids || [])].filter(Boolean);
    return new Set(ids);
  }, [selectedCategory]);

  const canPin = user.role === 'admin' || leadIds.has(user.id);

  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts;
    const query = searchQuery.toLowerCase();
    return posts.filter((post) =>
      post.title?.toLowerCase().includes(query) ||
      post.content?.toLowerCase().includes(query) ||
      post.username?.toLowerCase().includes(query)
    );
  }, [posts, searchQuery]);

  const { language } = useLanguage();
  const tr = useTranslatedMap([
    ...categories.flatMap((c) => [c.name, c.description]),
    ...filteredPosts.flatMap((p) => [p.title, p.content]),
    ...pinned.flatMap((p) => [p.title, p.content]),
  ]);

  useEffect(() => {
    fetchCategories();
    fetchMembershipStatus();
    axios.get(`${API}/users/connections`)
      .then((r) => setConnectedIds(new Set((r.data.connections || []).map((c) => c.id))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setForumPage(1);
  }, [selectedCategory?.id]);

  const fetchMembershipStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembershipType(response.data.membership_type);
    } catch (error) {
      console.error('Failed to fetch membership status');
    }
  };

  const lockedCount = categories.filter((c) => c.has_access === false).length;
  // Bronze is the free tier everyone starts on: if you can see a channel, you can post in it.
  const canPostHere = selectedCategory ? selectedCategory.has_access !== false : false;
  const isViewOnly = !canPostHere;

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`);
      setCategories(response.data);
      // A platform link can point straight at a chat: /dashboard/forum?category=…&post=…
      const wanted = new URLSearchParams(window.location.search).get('category');
      const target = wanted && response.data.find((c) => c.id === wanted);
      if (target && !selectedCategoryRef.current) {
        setSelectedCategory(target);
        return;
      }
      const firstOpen = response.data.find((c) => c.has_access !== false);
      if (firstOpen && !selectedCategoryRef.current) {
        setSelectedCategory(firstOpen);
      } else if (selectedCategoryRef.current) {
        // keep lead metadata fresh
        const updated = response.data.find((c) => c.id === selectedCategoryRef.current.id);
        if (updated) setSelectedCategory(updated);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchPosts = useCallback(async (categoryId, page, silent = false) => {
    try {
      const response = await axios.get(`${API}/forum/posts/${categoryId}?page=${page}&per_page=40`);
      setPosts(response.data.posts || []);
      setPinned(response.data.pinned || []);
      setForumTotalPages(response.data.total_pages || 1);
      setForumTotalPosts(response.data.total || 0);
    } catch (error) {
      if (!silent) toast.error('Failed to load messages');
    }
  }, []);

  useEffect(() => {
    if (selectedCategory) fetchPosts(selectedCategory.id, forumPage);
  }, [selectedCategory, forumPage, fetchPosts]);

  // Near-real-time polling (every 5s) — refresh the visible page silently
  useEffect(() => {
    const interval = setInterval(() => {
      const cat = selectedCategoryRef.current;
      if (cat) fetchPosts(cat.id, pageRef.current, true);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchPosts]);

  const sendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      await axios.post(`${API}/forum/posts`, {
        category_id: selectedCategory.id,
        title: '',
        content: messageText,
        parent_id: replyingTo?.id || null,
      });
      setMessageText('');
      setReplyingTo(null);
      setForumPage(1);
      fetchPosts(selectedCategory.id, 1);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleReact = async (postId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/forum/posts/${postId}/react`, { emoji }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPosts(selectedCategory.id, forumPage, true);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handlePin = async (postId) => {
    try {
      await axios.post(`${API}/forum/posts/${postId}/pin`);
      toast.success('Message pinned');
      fetchPosts(selectedCategory.id, forumPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin');
    }
  };

  const handleUnpin = async (postId) => {
    try {
      await axios.delete(`${API}/forum/posts/${postId}/pin`);
      toast.success('Message unpinned');
      fetchPosts(selectedCategory.id, forumPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpin');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Message deleted');
      fetchPosts(selectedCategory.id, forumPage);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleReportPost = (postId) => {
    setReportPostId(postId);
    setShowReportDialog(true);
  };

  const submitReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/forum/posts/${reportPostId}/report`,
        { reason: reportReason, description: reportDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report submitted. Our team will review it.');
      setShowReportDialog(false);
      setReportReason('');
      setReportDescription('');
      setReportPostId(null);
    } catch (error) {
      if (error.response?.status === 400) toast.error('You have already reported this message');
      else toast.error('Failed to submit report');
    }
  };

  const messageProps = (post) => ({
    post,
    user,
    tr,
    language,
    isLead: leadIds.has(post.user_id),
    isConnected: connectedIds.has(post.user_id),
    canPin,
    isViewOnly,
    onReact: handleReact,
    onPin: handlePin,
    onUnpin: handleUnpin,
    onReply: setReplyingTo,
    onDelete: handleDeletePost,
    onReport: handleReportPost,
  });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <TranslatableText as="h1" className="text-4xl sm:text-5xl font-bold text-white mb-2" text="Community Forum" />
        <TranslatableText as="p" className="text-lg text-gray-300" text="Connect, share, and grow together" />
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No channels available yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Channels Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-3 space-y-1">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 px-2 py-1">Channels</p>
                {categories.map((cat) => {
                  const hasAccess = cat.has_access !== false;
                  const active = selectedCategory?.id === cat.id;
                  return (
                    <button
                      key={cat.id}
                      data-testid={`category-${cat.id}`}
                      onClick={() => (hasAccess ? setSelectedCategory(cat) : setShowTierPaymentModal(true))}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                        active ? 'bg-cyan-100 text-cyan-700 font-semibold' : hasAccess ? 'hover:bg-gray-100' : 'opacity-60'
                      }`}
                      disabled={!hasAccess}
                    >
                      <Hash className="w-4 h-4 flex-shrink-0 opacity-60" />
                      <span className="flex-1 truncate">{tr(cat.name)}</span>
                      {cat.lead_practitioner_id && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                      {!hasAccess && <Lock className="w-4 h-4 text-gray-500" />}
                    </button>
                  );
                })}

                {lockedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowTierPaymentModal(true)}
                    className="w-full text-left mt-2 px-3 py-3 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
                    data-testid="forum-upgrade-notice"
                  >
                    <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4" />
                      {lockedCount} more channel{lockedCount === 1 ? '' : 's'} to explore
                    </p>
                    <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                      There is more conversation waiting at the next tier — see what opens up.
                    </p>
                  </button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-3">
            {selectedCategory && (
              <Card className="flex flex-col" style={{ minHeight: '70vh' }}>
                {/* Channel header */}
                <div className="border-b px-4 py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="font-bold text-gray-900 flex items-center gap-2">
                      <Hash className="w-4 h-4 opacity-50" />
                      {tr(selectedCategory.name)}
                    </h2>
                    <p className="text-xs text-gray-500 truncate">{tr(selectedCategory.description)}</p>
                  </div>
                  {pinned.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPinnedPanel((v) => !v)}
                      className="flex-shrink-0 text-amber-700 border-amber-300"
                      data-testid="pinned-toggle-btn"
                    >
                      <Pin className="w-4 h-4 mr-1" />
                      {pinned.length} Pinned
                    </Button>
                  )}
                </div>

                {/* Pinned panel */}
                {showPinnedPanel && pinned.length > 0 && (
                  <div className="border-b bg-amber-50/50 px-3 py-2 max-h-64 overflow-y-auto" data-testid="pinned-panel">
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-700 px-2 mb-1">Pinned Messages</p>
                    {pinned.map((p) => (
                      <ChatMessage key={`pin-${p.id}`} {...messageProps(p)} compact />
                    ))}
                  </div>
                )}

                {/* Search */}
                <div className="px-4 py-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search this channel..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-10 h-9"
                      data-testid="forum-search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Composer */}
                {canPostHere && (
                  <div className="border-b p-3 bg-white sticky top-0 z-10">
                    {replyingTo && (
                      <div className="flex items-center justify-between text-xs bg-cyan-50 border border-cyan-200 rounded-md px-3 py-1.5 mb-2" data-testid="reply-banner">
                        <span className="truncate text-cyan-800">
                          Replying to <span className="font-semibold">{replyingTo.username}</span>
                        </span>
                        <button onClick={() => setReplyingTo(null)} className="text-cyan-600 hover:text-cyan-800">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 relative">
                        <MentionAutocomplete
                          placeholder={`Message #${selectedCategory.name}...  (Type @ to mention)`}
                          rows={1}
                          value={messageText}
                          onChange={(value) => setMessageText(value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <div className="absolute bottom-1.5 right-2">
                          <SpeechToTextIcon
                            onTranscript={(text) => setMessageText((prev) => prev + ' ' + text)}
                            className="scale-75"
                          />
                        </div>
                      </div>
                      <EmojiPickerButton onEmojiSelect={(emoji) => setMessageText((prev) => prev + emoji)} />
                      <Button
                        data-testid="chat-send-btn"
                        onClick={sendMessage}
                        className="btn-primary h-10"
                        disabled={!messageText.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                {/* Message stream (newest first) */}
                <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5" data-testid="chat-stream">
                  {filteredPosts.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      {searchQuery ? (
                        <>
                          <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No messages match &ldquo;{searchQuery}&rdquo;</p>
                          <Button onClick={() => setSearchQuery('')} variant="outline" className="mt-3">
                            Clear Search
                          </Button>
                        </>
                      ) : (
                        <>
                          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No messages yet. Start the conversation!</p>
                        </>
                      )}
                    </div>
                  ) : (
                    filteredPosts.map((post) => (
                      <div
                        key={post.id}
                        id={`post-${post.id}`}
                        className={linkedPostId === post.id
                          ? 'rounded-xl ring-2 ring-amber-400/70 bg-amber-50/40 transition-all'
                          : ''}
                      >
                        <ChatMessage {...messageProps(post)} />
                      </div>
                    ))
                  )}

                  {forumTotalPages > 1 && !searchQuery && (
                    <div className="flex flex-col items-center gap-2 mt-4 pb-2" data-testid="forum-pagination-wrapper">
                      <div className="text-xs text-gray-400">
                        Page {forumPage} of {forumTotalPages} · {forumTotalPosts} messages
                      </div>
                      <Pagination
                        page={forumPage}
                        totalPages={forumTotalPages}
                        onChange={setForumPage}
                        testidPrefix="forum-pagination"
                        theme="light"
                      />
                    </div>
                  )}
                </div>

              </Card>
            )}
          </div>
        </div>
      )}

      <TierPaymentModal
        isOpen={showTierPaymentModal}
        onClose={() => setShowTierPaymentModal(false)}
        onSuccess={() => {
          setShowTierPaymentModal(false);
          fetchCategories();
          fetchMembershipStatus();
        }}
        currentTier={user?.membership_tier || 'bronze'}
      />

      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Reason for reporting *</label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="harassment">Harassment or Bullying</SelectItem>
                  <SelectItem value="spam">Spam or Advertising</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="hate_speech">Hate Speech or Discrimination</SelectItem>
                  <SelectItem value="violence">Violence or Threats</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Additional details (optional)</label>
              <Textarea
                placeholder="Provide more context..."
                rows={4}
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={submitReport} className="flex-1 btn-primary">Submit Report</Button>
              <Button
                onClick={() => {
                  setShowReportDialog(false);
                  setReportReason('');
                  setReportDescription('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumPage;
